import { getColumnInfo } from '../../../../data/analyticsDataSlice';
import { SORTDIR, TpDataContext, valtypes_sortable } from '../../../../data/interfaces';
import {
  CONFIGPARAMTYPES,
  createConfigParamBoolean,
  createConfigParamMultipleChoice,
} from '../../helpers/configParams';
import { getDataTable, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';

import { DOCVIEWER_ELEMENT_TYPES, TpChartManifest } from '../interfaces';
import { DataTable } from './DataTable';
import { ACTION_STYLES, ACTION_TYPES } from './interfaces';

const categoryColorsConfigParamType = {
  type: 'array',
  description: 'Color values in case useBadge is enabled.',
  items: {
    type: 'object',
    title: 'Category color',
    description: 'Mapping of a single categorical value to a color.',
    properties: {
      content: { type: 'string', description: 'categorical value to be mapped to a color' },
      backgroundColor: {
        type: 'object',
        description:
          'Background color to be used for displaying the column values corresponding to the specified categorical value',
        properties: {
          r: { type: 'number', description: 'Red channel value (between 0 and 255)' },
          g: { type: 'number', description: 'Green channel value (between 0 and 255)' },
          b: { type: 'number', description: 'Blue channel value (between 0 and 255)' },
        },
      },
    },
  },
};

const numericalBarConfigParamType = {
  type: 'object',
  description: 'Display a numerical value as a bar.',
  properties: {
    minVal: {
      type: 'number',
      description: 'Minimum value for the numerical bar (corresponding to an empty bar)',
    },
    maxVal: {
      type: 'number',
      description: 'Maximum value for the numerical bar (corresponding to a full bar)',
    },
  },
  required: ['minVal', 'maxVal'],
};

const columnActionConfigParamType = {
  type: 'object',
  description: 'Action to be performed when clicking on a column value.',
  properties: {
    type: {
      type: 'string',
      description: 'Type of the action to be performed.',
      enum: ACTION_TYPES,
    },
    requiredProperty: {
      type: 'string',
      description:
        'Identifier of the property whose value should be used for performing the action (defaults to the current property).',
    },
    requiredPropertyValues: {
      type: 'array',
      description:
        'In case the action requires a specific value for a certain property, this field specifies the required value(s) for that property.',
      items: { type: 'string' },
    },
    actionStyle: {
      type: 'string',
      description: 'Style in which the action should be displayed in the table.',
      enum: ACTION_STYLES,
    },
    actionIcon: {
      type: 'string',
      description:
        'Icon to be used for the action (overrides the default icon for the action type). Define as prefix:iconName (e.g. "fas:folder-open").',
    },
    actionLabel: {
      type: 'string',
      description: 'Tooltip text for the action (overrides the default label for the action type).',
    },
  },
  required: ['type'],
};

const columnConfigParamType = {
  configParamTypeId: CONFIGPARAMTYPES.JSON,
  schema: {
    type: 'array',
    items: {
      type: 'object',
      title: 'Column',
      description: 'Property to be used as a column in the table',
      properties: {
        id: { type: 'string', description: 'column header/column id' },
        width: { type: 'number', description: 'column width' },
        useBadge: { type: 'boolean', description: 'whether to display the column values as badges' },
        bold: { type: 'boolean', description: 'whether to display the column values in bold font' },
        categoryColors: categoryColorsConfigParamType,
        numericalBar: numericalBarConfigParamType,
        iconMapper: {
          type: 'object',
          title: 'Icon mapper',
          description: 'Display categorical values as icons, based on the provided mapping.',
          additionalProperties: true, // the keys of this object are the categorical values, and the value for each key is the corresponding icon name to be displayed
        },
        action: columnActionConfigParamType,
      },
      required: ['id'],
    },
  },
};

export const manifestDataTable: TpChartManifest = {
  id: 'DataTable',
  name: 'Data table',
  description: `
Displays the entities of a data table in a tabular format, with a columns corresponding to a specified set of properties.
The table is paged and can be searched in by the user.
`,
  component: DataTable,
  canvasBased: false,
  groups: [
    { groupId: 'data', groupName: 'Data' },
    { groupId: 'sort', groupName: 'Sort' },
  ],

  aspects: [
    {
      id: 'sortBy',
      name: 'Sort by',
      groupId: 'sort',
      description: 'Property used to sort the entities in the table',
      required: false,
      quality: 0,
      dataType: valtypes_sortable,
    },
  ],
  configParams: [
    {
      id: 'sortDir',
      name: 'Direction',
      description: 'Sort direction of the entities in the table',
      groupId: 'sort',
      paramType: createConfigParamMultipleChoice([
        { id: SORTDIR.ASC, name: 'Ascending' },
        { id: SORTDIR.DESC, name: 'Descending' },
      ]),
    },
    {
      id: 'showGroupHeaders',
      name: 'Show Group Headers',
      description: 'Specifies whether or not the group headers are displayed in the table.',
      groupId: 'sort',
      paramType: createConfigParamBoolean(false),
    },
    {
      id: 'columns',
      name: 'Columns',
      description: `
List of identifiers of the properties to be used as colunns in the table.
This should be entered as a JSON structure, containing a lit of all properties to be displayed.
For each property, \`id\` specifies the property identifier. Optionally, \`width\` can specify the column width in the table.

Example:
\`\`\`
- id: property1
- id: property2
  width: 250
\`\`\`
`,
      groupId: 'data',
      paramType: columnConfigParamType,
      hidden: true,
      documentation: createColumnsDocViewerElements,
    },
    {
      id: 'includeUnselected',
      name: 'Include unselected',
      description: 'Specifies whether or not unselected entities are displayed in the table.',
      groupId: 'sort',
      paramType: createConfigParamBoolean(false),
    },
  ],
  examples: [
    {
      id: 'datatable_1',
    },
  ],
};

function createColumnsDocViewerElements(
  currentConfigParams: TpChartConfigParams,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
) {
  const dataTable = getDataTable(dataContext, currentDataBinding.dataTableId);

  const columns = (currentConfigParams['columns'] ?? [])
    .map((column: any) => {
      const columnInfo = getColumnInfo(dataTable, column.id);
      if (!columnInfo) {
        return null;
      }
      return {
        name: columnInfo.name,
        description: columnInfo.description || null,
      };
    })
    .filter((column: any) => column !== null);

  return [
    {
      type: DOCVIEWER_ELEMENT_TYPES.PROPERTIES,
      id: 'columns',
      content: columns,
    },
  ];
}
